/**
 * Admin API client (Backoffice MVP — Plan BO-01).
 *
 * Wraps the `/v1/admin/*` endpoints with Firebase Bearer auth. The
 * caller must hold the `sudo: true` custom claim — `<SudoGuard>`
 * already enforces this client-side, but the BE rejects with 403
 * regardless if the claim is missing.
 *
 * Why a separate file: keeping admin endpoints isolated avoids
 * accidentally mixing recruiter-facing types and pollutes
 * autocompletion in `services/api.ts` for the 99% non-admin case.
 */
import { auth } from '@/lib/firebase';

const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  'https://europe-west3-recruiterai-8d65b.cloudfunctions.net/api/v1';

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}

async function authedJson<T>(path: string): Promise<T> {
  const res = await authedFetch(path);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Admin API ${res.status}: ${text}`);
  }
  return (await res.json()) as T;
}

/**
 * Helper for write requests (POST/PATCH/PUT/DELETE) that all admin
 * mutations share. The body is serialised once; errors surface the
 * BE message verbatim so the FE can show specific reasons (e.g.
 * `REASON_REQUIRED`, `CONFIRM_MISMATCH`, `IMPERSONATION_READ_ONLY`).
 */
async function authedMutate<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await authedFetch(path, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Admin API ${res.status}: ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminDashboardKpi {
  signupsToday: number;
  trialsEndingNext7d: number;
  mrrProxy: number;
  mrrCurrency: string;
  recentActivity: Array<{
    companyId: string;
    name: string;
    kind: 'signup' | 'update';
    at?: string;
    planId?: string;
    segment?: string;
    trialStatus?: string;
    isActive?: boolean;
  }>;
}

export interface AdminCustomerRow {
  id: string;
  name: string;
  publicName: string;
  segment: string;
  planId?: string;
  isActive: boolean;
  trialStatus?: string;
  trialEndsAt?: string;
  ownerEmail?: string;
  ownerName?: string;
  ownerUid?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminCustomerDetail {
  id: string;
  name: string;
  publicName: string;
  slug: string;
  description?: string;
  employeeCount?: number;
  logoUrl?: string;
  isActive: boolean;
  segment: string;
  parentAgencyId?: string;
  billing?: { planId?: string; planUpdatedAt?: string };
  trial?: { status?: string; startedAt?: string; endsAt?: string };
  settings?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  kpi: {
    positionsCount: number;
    postingsCount?: number;
    applicationsCount: number;
    membersCount: number;
  };
  owners: Array<{ uid?: string; email?: string; displayName?: string }>;
}

export interface AdminSubscriptionRow {
  companyId: string;
  name: string;
  publicName: string;
  segment: string;
  planId: string | null;
  planUpdatedAt?: string;
  trialStatus: string | null;
  trialEndsAt?: string;
  derivedStatus: 'trial' | 'active' | 'expired' | 'none';
  ownerEmail?: string;
}

export interface AdminPostingRow {
  postingId: string;
  companyId?: string;
  companyName?: string;
  jobPositionId?: string;
  positionTitle?: string;
  title: string;
  channel: string;
  isActive: boolean;
  createdAt?: string;
  closedAt?: string;
}

export interface AdminApplicationRow {
  applicationId: string;
  companyId?: string;
  companyName?: string;
  jobPositionId?: string;
  positionTitle?: string;
  jobPostingId: string;
  candidateName: string;
  candidateEmail: string;
  channel: string;
  state: string;
  lastFailureReason: string | null;
  appliedAt?: string;
}

export interface AdminPostingSibling {
  postingId: string;
  title: string;
  channel: string;
  slug: string | null;
  isActive: boolean;
  visibility: string | null;
  url: string | null;
  createdAt?: string;
  publishedAt?: string;
  closedAt?: string;
  /** True se questo è il posting attualmente visualizzato (highlight UI) */
  isCurrent: boolean;
}

export interface AdminPositionContext {
  id: string;
  title: string;
  description: string | null;
  seniority: string | null;
  contractType: string | null;
  workMode: string | null;
  location: string | null;
  salaryMin: number | null;
  salaryMax: number | null;
  salaryCurrency: string | null;
  status: string | null;
  outputLanguage: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminPostingDetail {
  postingId: string;
  companyId: string;
  jobPositionId: string;
  companyName: string;
  companySlug: string | null;
  positionTitle: string;
  title: string;
  channel: string;
  slug: string | null;
  url: string | null;
  isActive: boolean;
  visibility: string | null;
  createdAt?: string;
  updatedAt?: string;
  closedAt?: string;
  publishedAt?: string;
  /** Position parent context per render UI strutturata (sprint admin_posting_detail_richness 30/05/26) */
  position?: AdminPositionContext;
  /** Tutti i posting della stessa position (canali di pubblicazione) */
  siblings?: AdminPostingSibling[];
  siblingsCount?: number;
  raw: Record<string, unknown>;
}

export interface AdminApplicationDetail {
  applicationId: string;
  companyId: string;
  jobPositionId: string;
  jobPostingId: string;
  companyName: string;
  positionTitle: string;
  candidate: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    cvUrl: string;
  };
  candidateName: string;
  candidateEmail: string;
  channel: string;
  state: string;
  lastFailureReason: string | null;
  scores?: Record<string, number> | null;
  appliedAt?: string;
  updatedAt?: string;
  raw: Record<string, unknown>;
}

export interface AdminUserRow {
  uid: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  disabled: boolean;
  hasSudo: boolean;
  accountType?: string;
  agencyPlanId?: string;
  membershipsCount: number;
  membershipCompanyIds: string[];
  /** Mappa companyId → name resolved dal BE (sprint admin_users_company_names 30/05/26). */
  membershipCompanyNames?: Record<string, string>;
  createdAt?: string;
  lastSignInAt?: string;
  authCreatedAt?: string;
}

export interface AdminSetSudoResult {
  uid: string;
  hasSudo: boolean;
  claims: Record<string, unknown>;
  tokenRefreshRequired: boolean;
}

export interface AdminUserDetail {
  uid: string;
  email: string;
  displayName: string;
  accountType?: string;
  agencyPlanId?: string;
  agencyBillingPeriod?: string;
  locale?: string;
  timezone?: string;
  avatarUrl?: string;
  status?: string;
  memberships: Record<string, unknown>;
  membershipsCount: number;
  createdAt?: string;
  updatedAt?: string;
  auth: {
    emailVerified: boolean;
    disabled: boolean;
    phoneNumber: string | null;
    providers: string[];
    customClaims: Record<string, unknown>;
    lastSignInAt: string | null;
    authCreatedAt: string | null;
  } | null;
}

export interface AdminImpersonateResult {
  customToken: string;
  targetUid: string;
  targetEmail: string | null;
  mode: 'read_only';
  expiresInSec: number;
  expiresAt: string;
}

export interface AdminSubscriptionView {
  companyId: string;
  planId: string;
  segment: string;
  parentAgencyId: string | null;
  billing: { planId: string; planUpdatedAt?: string };
  trial: {
    status: string;
    startedAt?: string;
    endsAt?: string;
    convertedAt?: string;
    daysLeft: number | null;
  } | null;
  autumn: {
    customerId: string;
    status: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    paymentMethod: { brand: string | null; last4: string | null; expMonth: number | null; expYear: number | null } | null;
    mrrAmount: number;
    arrAmount: number;
    nextPaymentAt: string | null;
    nextPaymentAmount: number | null;
    totalPaidLifetime: number;
    currency: string;
  } | null;
}

export interface AdminInvoice {
  id: string;
  customerId: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  amountDueDecimal: number;
  amountPaidDecimal: number;
  currency: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  number: string | null;
  created: string;
  paidAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  description?: string | null;
}

export interface AdminMrrBreakdown {
  totalMrr: number;
  totalArr: number;
  byPlan: Array<{ planId: string; count: number; mrr: number; arr: number }>;
  bySegment: Array<{ segment: string; count: number; mrr: number; arr: number }>;
  computedAt: string;
}

export interface AdminRecentPayment {
  companyId: string;
  companyName: string;
  invoiceId: string;
  amountPaid: number;
  currency: string;
  status: string;
  created: string;
  hostedInvoiceUrl: string | null;
}

export interface AdminPlanLimits {
  seats: number;
  positions: number;
  candidates: number;
  aiCompany: number;
  /** @deprecated v3.5 — aiUser pool eliminato. */
  aiUser?: number;
  transcripts: number;
  agencyClients?: number;
  recruiterSeatsMin?: number;
}

export interface AdminPlanPricing {
  priceMonthly: number;
  priceAnnual?: number;
  hasAnnual: boolean;
}

export interface AdminPlanDescriptor {
  segment: 'sme' | 'agency' | 'enterprise';
  limits: AdminPlanLimits;
  pricing: AdminPlanPricing;
  isCustom?: boolean;
  trialDays?: number;
  scopeAgencyId?: string;
  deprecated?: boolean;
}

export interface AdminPlanRow {
  planId: string;
  isStatic: boolean;
  segment: string;
  priceMonthly: number;
  priceAnnual?: number;
  hasAnnual: boolean;
  isCustom: boolean;
  deprecated: boolean;
  scopeAgencyId?: string;
  trialDays?: number;
  limits: AdminPlanLimits;
  companiesCount: number;
}

export interface AdminActionRow {
  id: string;
  adminUid: string;
  adminEmail: string | null;
  action: string;
  target: { kind: string; id: string; label?: string };
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  ip: string | null;
  userAgent: string | null;
  at?: string;
}

/** Sprint 6 — Email audit row (sentEmails collection). */
export interface EmailAuditRow {
  id: string;
  ts: string | null;
  template: string | null;
  locale: string | null;
  /**
   * Email destinatario plaintext (salvato da 30/05/26 in poi —
   * sprint email_audit_show_recipient). Log pre-fix hanno solo `toMasked`,
   * il FE fa fallback a quello.
   */
  to: string | null;
  toMasked: string | null;
  companyId: string | null;
  recipientType: string | null;
  status: 'sent' | 'delivered' | 'failed' | 'suppressed' | 'bounced' | 'complained' | null;
  provider: string;
  messageId: string | null;
  error: string | null;
  env: string | null;
  lastWebhookAt: string | null;
}

export interface AdminSearchResults {
  customers: Array<{
    companyId: string;
    name: string;
    publicName: string;
    segment?: string;
    planId?: string;
    isActive: boolean;
  }>;
  users: Array<{
    uid: string;
    email?: string;
    displayName?: string;
    accountType?: string;
  }>;
  count: number;
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

function buildQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
}

export const adminApi = {
  dashboard: {
    kpi: () => authedJson<AdminDashboardKpi>('/admin/dashboard/kpi'),
  },
  customers: {
    list: (filters: {
      segment?: string;
      plan?: string;
      isActive?: boolean;
      trialStatus?: string;
      createdFrom?: string;
      createdTo?: string;
      limit?: number;
      cursor?: string | null;
      sortBy?: string;
      sortDir?: 'asc' | 'desc';
    } = {}) =>
      authedJson<{
        items: AdminCustomerRow[];
        nextCursor: string | null;
        hasMore?: boolean;
        totalCount?: number;
        count: number;
      }>(
        '/admin/customers' + buildQueryString(filters),
      ),
    detail: (companyId: string) =>
      authedJson<AdminCustomerDetail>(`/admin/customers/${companyId}`),
    subscriptions: (companyId: string) =>
      authedJson<{
        companyId: string;
        billing?: { planId?: string };
        trial?: { status?: string; endsAt?: string };
        segment: string;
        parentAgencyId?: string;
      }>(`/admin/customers/${companyId}/subscriptions`),
    audit: (companyId: string, limit = 30) =>
      authedJson<{
        items: Array<{
          id: string;
          adminUid: string;
          adminEmail: string | null;
          method: string;
          route: string;
          kind: 'read' | 'write';
          ip: string;
          at?: string;
        }>;
        count: number;
      }>(`/admin/customers/${companyId}/audit?limit=${limit}`),
    // BO-04 write actions
    update: (
      companyId: string,
      payload: Partial<{
        name: string;
        publicName: string;
        description: string;
        employeeCount: number;
        logoUrl: string | null;
        salary: { min?: number; max?: number; currency?: string } | null;
      }> & { reason: string },
    ) => authedMutate<Record<string, unknown>>('PATCH', `/admin/customers/${companyId}`, payload),
    suspend: (companyId: string, reason: string) =>
      authedMutate<{ id: string; isActive: boolean }>(
        'POST',
        `/admin/customers/${companyId}/suspend`,
        { reason },
      ),
    unsuspend: (companyId: string, reason: string) =>
      authedMutate<{ id: string; isActive: boolean }>(
        'POST',
        `/admin/customers/${companyId}/unsuspend`,
        { reason },
      ),
    softDelete: (companyId: string, reason: string) =>
      authedMutate<{ id: string; deleted: boolean }>(
        'DELETE',
        `/admin/customers/${companyId}`,
        { reason },
      ),
    permanentDelete: (companyId: string, reason: string, confirm: string) =>
      authedMutate<{ id: string; deleted: boolean }>(
        'DELETE',
        `/admin/customers/${companyId}/permanent`,
        { reason, confirm },
      ),
    /**
     * Bulk permanent delete companies (Admin SUPER-MVP M3, 27/05/26).
     * BE valida body.confirm === 'BULK' (anti fat-finger).
     * Cascade delete subcollections via recursiveDelete.
     * Max 100 ids per batch.
     */
    bulkDelete: (ids: string[], reason: string) =>
      authedMutate<{
        summary: { total: number; deleted: number; failed: number };
        deletedIds: string[];
        failures: Array<{ id: string; error: string }>;
      }>('POST', `/admin/customers/bulk/delete`, { ids, reason, confirm: 'BULK' }),
  },
  subscriptions: {
    list: (
      filters: {
        status?: string;
        plan?: string;
        segment?: string;
        endingSoon?: boolean;
        limit?: number;
        cursor?: string | null;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
      } = {},
    ) =>
      authedJson<{
        items: AdminSubscriptionRow[];
        count: number;
        nextCursor?: string | null;
        hasMore?: boolean;
        totalCount?: number;
      }>(
        '/admin/subscriptions' + buildQueryString(filters),
      ),
  },
  postings: {
    list: (
      filters: {
        companyId?: string;
        isActive?: boolean;
        channel?: string;
        titleSearch?: string;
        createdFrom?: string;
        createdTo?: string;
        limit?: number;
        cursor?: string | null;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
      } = {},
    ) =>
      authedJson<{
        items: AdminPostingRow[];
        count: number;
        nextCursor?: string | null;
        hasMore?: boolean;
        totalCount?: number;
      }>(
        '/admin/postings' + buildQueryString(filters),
      ),
    detail: (companyId: string, posId: string, postingId: string) =>
      authedJson<AdminPostingDetail>(
        `/admin/customers/${companyId}/positions/${posId}/postings/${postingId}`,
      ),
    update: (
      companyId: string,
      posId: string,
      postingId: string,
      payload: Partial<{ title: string; channel: string }> & { reason: string },
    ) =>
      authedMutate<Record<string, unknown>>(
        'PATCH', `/admin/customers/${companyId}/positions/${posId}/postings/${postingId}`, payload,
      ),
    close: (companyId: string, posId: string, postingId: string, reason: string) =>
      authedMutate<Record<string, unknown>>(
        'PATCH', `/admin/customers/${companyId}/positions/${posId}/postings/${postingId}/close`, { reason },
      ),
    reopen: (companyId: string, posId: string, postingId: string, reason: string) =>
      authedMutate<Record<string, unknown>>(
        'PATCH', `/admin/customers/${companyId}/positions/${posId}/postings/${postingId}/reopen`, { reason },
      ),
    regenerateUrl: (companyId: string, posId: string, postingId: string, reason: string) =>
      authedMutate<{ id: string; landingPageUrl: string }>(
        'POST', `/admin/customers/${companyId}/positions/${posId}/postings/${postingId}/regenerate-url`, { reason },
      ),
  },
  positions: {
    detail: (companyId: string, posId: string) =>
      authedJson<{
        id: string;
        companyId: string;
        companyName: string;
        title?: string;
        description?: string;
        status?: string;
        kpi: { postingsCount: number; applicationsCount: number; membersCount: number };
        raw: Record<string, unknown>;
      }>(`/admin/customers/${companyId}/positions/${posId}`),
    update: (
      companyId: string,
      posId: string,
      payload: Record<string, unknown> & { reason: string },
    ) =>
      authedMutate<Record<string, unknown>>(
        'PATCH', `/admin/customers/${companyId}/positions/${posId}`, payload,
      ),
    setStatus: (companyId: string, posId: string, status: 'active' | 'closed', reason: string) =>
      authedMutate<Record<string, unknown>>(
        'PATCH', `/admin/customers/${companyId}/positions/${posId}/status`, { status, reason },
      ),
    softDelete: (companyId: string, posId: string, reason: string) =>
      authedMutate<{ id: string; deleted: boolean }>(
        'DELETE', `/admin/customers/${companyId}/positions/${posId}`, { reason },
      ),
  },
  applications: {
    list: (
      filters: {
        state?: string;
        lastFailureReason?: string;
        companyId?: string;
        candidateEmail?: string;
        channel?: string;
        appliedFrom?: string;
        appliedTo?: string;
        limit?: number;
        cursor?: string | null;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
      } = {},
    ) =>
      authedJson<{
        items: AdminApplicationRow[];
        count: number;
        nextCursor?: string | null;
        hasMore?: boolean;
        totalCount?: number;
      }>(
        '/admin/applications' + buildQueryString(filters),
      ),
    detail: (companyId: string, posId: string, appId: string) =>
      authedJson<AdminApplicationDetail>(
        `/admin/customers/${companyId}/positions/${posId}/applications/${appId}`,
      ),
  },
  users: {
    list: (
      filters: {
        accountType?: string;
        hasSudo?: boolean;
        search?: string;
        limit?: number;
        cursor?: string | null;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
      } = {},
    ) =>
      authedJson<{
        items: AdminUserRow[];
        nextCursor?: string | null;
        hasMore?: boolean;
        totalCount?: number;
        count: number;
      }>(
        '/admin/users' + buildQueryString(filters),
      ),
    detail: (uid: string) =>
      authedJson<AdminUserDetail>(`/admin/users/${uid}`),
    setSudo: async (uid: string, enabled: boolean): Promise<AdminSetSudoResult> => {
      const res = await authedFetch(`/admin/users/${uid}/sudo`, {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`setSudo ${res.status}: ${text}`);
      }
      return (await res.json()) as AdminSetSudoResult;
    },
    update: (
      uid: string,
      payload: Partial<{
        displayName: string;
        accountType: string;
        agencyPlanId: string;
        agencyBillingPeriod: string;
        locale: string;
        timezone: string;
        avatarUrl: string;
      }> & { reason: string },
    ) => authedMutate<Record<string, unknown>>('PATCH', `/admin/users/${uid}`, payload),
    agencyCheckoutLink: (
      uid: string,
      payload: {
        agencyPlanId: string;
        desiredCompanies: number;
        successUrl?: string;
        cancelSmeAtPeriodEnd?: boolean;
        reason: string;
      },
    ) =>
      authedMutate<{
        ownerUid: string;
        agencyPlanId: string;
        desiredCompanies: number;
        checkoutUrl: string | null;
        warnings: string[];
        primaryCompanyId?: string;
      }>('POST', `/admin/users/${uid}/agency-checkout-link`, payload),
    disable: (uid: string, reason: string) =>
      authedMutate<{ uid: string; disabled: boolean }>('POST', `/admin/users/${uid}/disable`, { reason }),
    enable: (uid: string, reason: string) =>
      authedMutate<{ uid: string; disabled: boolean }>('POST', `/admin/users/${uid}/enable`, { reason }),
    resetPassword: (uid: string, reason: string) =>
      authedMutate<{ uid: string; resetLink: string; sentTo: string }>(
        'POST',
        `/admin/users/${uid}/reset-password`,
        { reason },
      ),
    verifyEmail: (uid: string, reason: string) =>
      authedMutate<{ uid: string; emailVerified: boolean }>(
        'POST',
        `/admin/users/${uid}/verify-email`,
        { reason },
      ),
    impersonate: (uid: string, reason: string) =>
      authedMutate<AdminImpersonateResult>('POST', `/admin/users/${uid}/impersonate`, { reason }),
    /**
     * Force-delete a user account (Sprint 4 S4.A).
     * Backend enforces typing-confirm: body.confirm MUST equal the uid exactly.
     * Returns 204 on success.
     */
    forceDelete: (uid: string, reason: string) =>
      authedMutate<{ uid: string; deleted: boolean }>(
        'POST',
        `/admin/users/${uid}/delete`,
        { reason, confirm: uid },
      ),
    /**
     * Bulk force-delete users (Admin SUPER-MVP M3, 27/05/26).
     * BE valida body.confirm === 'BULK'. Skip self + skip altri sudo admin.
     * Cascade per uid: memberships company + jobPosition + users/{uid}
     * subcollections (notifications, ecc.) via recursiveDelete + Firebase Auth.
     * Max 100 uid per batch.
     */
    bulkDelete: (ids: string[], reason: string) =>
      authedMutate<{
        summary: { total: number; deleted: number; failed: number };
        deletedIds: string[];
        failures: Array<{ id: string; error: string }>;
      }>('POST', `/admin/users/bulk/delete`, { ids, reason, confirm: 'BULK' }),
  },
  adminActions: {
    list: (
      filters: {
        adminUid?: string;
        action?: string;
        kind?: string;
        targetId?: string;
        from?: string;
        to?: string;
        limit?: number;
        cursor?: string | null;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
      } = {},
    ) =>
      authedJson<{
        items: AdminActionRow[];
        nextCursor?: string | null;
        hasMore?: boolean;
        totalCount?: number;
        count: number;
      }>(
        '/admin/admin-actions' + buildQueryString(filters),
      ),
  },
  // Sprint 6 — Email audit dashboard
  emailAudit: {
    list: (
      filters: {
        status?: string;
        template?: string;
        recipientType?: string;
        companyId?: string;
        since?: string;
        until?: string;
        limit?: number;
        cursor?: string | null;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
      } = {},
    ) =>
      authedJson<{
        items: EmailAuditRow[];
        nextCursor: string | null;
        hasMore: boolean;
        totalCount?: number;
        filters: Record<string, unknown>;
      }>('/admin/email-audit' + buildQueryString(filters)),
    kpi: (days = 7) =>
      authedJson<{
        windowDays: number;
        since: string;
        totalAttempts: number;
        totals: Record<'sent' | 'delivered' | 'failed' | 'suppressed' | 'bounced' | 'complained', number>;
        deliveryRate: number;
        bounceRate: number;
        complaintRate: number;
      }>('/admin/email-audit/kpi' + buildQueryString({ days })),
  },
  // BO-06 — Subscription deep view + write actions (singular: per-company actions)
  subscription: {
    detail: (companyId: string) =>
      authedJson<AdminSubscriptionView>(`/admin/customers/${companyId}/subscription`),
    invoices: (companyId: string, limit = 50) =>
      authedJson<{ items: AdminInvoice[]; count: number; customerId: string }>(
        `/admin/customers/${companyId}/subscription/invoices?limit=${limit}`,
      ),
    portal: (companyId: string, returnUrl?: string) =>
      authedJson<{ url: string; customerId: string }>(
        `/admin/customers/${companyId}/subscription/portal${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`,
      ),
    refresh: (companyId: string, reason: string) =>
      authedMutate<{ companyId: string; planId: string }>(
        'POST', `/admin/customers/${companyId}/subscription/refresh`, { reason },
      ),
    extendTrial: (companyId: string, days: number, reason: string) =>
      authedMutate<{ companyId: string; previousEndsAt: string; newEndsAt: string; addedDays: number }>(
        'POST', `/admin/customers/${companyId}/subscription/extend-trial`, { days, reason },
      ),
    /**
     * BUG-11 fix: backend now returns autumnSynced + warning + period.
     * Pass `period: 'annual'` for yearly billing (default monthly).
     * Pass `forceFirestoreOnly: true` only when re-trying after a 502
     * AUTUMN_ATTACH_FAILED error and the admin accepts the desync risk.
     */
    changePlan: (
      companyId: string,
      planId: string,
      reason: string,
      options: { period?: 'monthly' | 'annual'; forceFirestoreOnly?: boolean } = {},
    ) =>
      authedMutate<{
        companyId: string;
        newPlanId: string;
        period: 'monthly' | 'annual';
        autumnSynced: boolean;
        warning?: string;
      }>(
        'POST',
        `/admin/customers/${companyId}/subscription/change-plan`,
        { planId, reason, period: options.period ?? 'monthly', forceFirestoreOnly: options.forceFirestoreOnly === true },
      ),
    cancel: (companyId: string, immediate: boolean, reason: string) =>
      authedMutate<{ companyId: string; planId: string; immediate: boolean }>(
        'POST', `/admin/customers/${companyId}/subscription/cancel`, { immediate, reason },
      ),
    uncancel: (companyId: string, reason: string) =>
      authedMutate<{ companyId: string; planId: string }>(
        'POST', `/admin/customers/${companyId}/subscription/uncancel`, { reason },
      ),
    grantUnlimited: (companyId: string, reason: string) =>
      authedMutate<{ companyId: string; planId: string }>(
        'POST', `/admin/customers/${companyId}/subscription/grant-unlimited`, { reason },
      ),
    resetCounters: (companyId: string, counters: string[], reason: string) =>
      authedMutate<{ companyId: string; reset: string[] }>(
        'POST', `/admin/customers/${companyId}/subscription/reset-counters`, { counters, reason },
      ),
  },
  finance: {
    mrrBreakdown: () => authedJson<AdminMrrBreakdown>('/admin/finance/mrr-breakdown'),
    recentPayments: () => authedJson<{ items: AdminRecentPayment[]; count: number }>(
      '/admin/finance/recent-payments',
    ),
  },
  // BO-07 — Plans catalog editor
  plans: {
    list: (filters: { segment?: string; isCustom?: boolean; includeDeprecated?: boolean } = {}) =>
      authedJson<{ items: AdminPlanRow[]; count: number }>(
        '/admin/plans' + buildQueryString(filters),
      ),
    detail: (planId: string) =>
      authedJson<{
        planId: string;
        isStatic: boolean;
        descriptor: AdminPlanDescriptor;
        version: number;
        updatedAt: string | null;
        updatedBy: string | null;
      }>(`/admin/plans/${planId}`),
    update: (planId: string, descriptor: Partial<AdminPlanDescriptor>, reason: string) =>
      authedMutate<{ planId: string; descriptor: AdminPlanDescriptor }>(
        'PATCH', `/admin/plans/${planId}`, { ...descriptor, reason },
      ),
    create: (planId: string, descriptor: AdminPlanDescriptor, reason: string) =>
      authedMutate<{ planId: string; descriptor: AdminPlanDescriptor }>(
        'POST', `/admin/plans`, { planId, descriptor, reason },
      ),
    deprecate: (planId: string, reason: string) =>
      authedMutate<{ planId: string; deprecated: boolean }>(
        'DELETE', `/admin/plans/${planId}`, { reason },
      ),
    companies: (planId: string) =>
      authedJson<{
        items: Array<{ companyId: string; name: string; publicName: string; segment: string; isActive: boolean }>;
        count: number;
      }>(`/admin/plans/${planId}/companies`),
  },
  systemConfig: {
    list: (prefix?: string) =>
      authedJson<{
        items: Array<{
          key: string;
          value: unknown;
          version: number;
          updatedAt: string | null;
          updatedBy: string | null;
          hasSchema: boolean;
        }>;
        count: number;
      }>('/admin/system-config' + (prefix ? `?prefix=${encodeURIComponent(prefix)}` : '')),
    detail: (key: string) =>
      authedJson<{
        key: string;
        value: unknown;
        version: number;
        updatedAt: string | null;
        updatedBy: string | null;
        hasSchema: boolean;
        history: Array<{ value: unknown; at: string; by: string; reason?: string }>;
      }>(`/admin/system-config/${encodeURIComponent(key)}`),
    update: (key: string, value: unknown, reason: string) =>
      authedMutate<{ key: string; value: unknown }>(
        'PUT', `/admin/system-config/${encodeURIComponent(key)}`, { value, reason },
      ),
    invalidate: (key: string | undefined, reason: string) =>
      authedMutate<{ invalidated: string }>('POST', '/admin/system-config/invalidate', { key, reason }),
  },
  search: (q: string, type: 'customer' | 'user' = 'customer') =>
    authedJson<AdminSearchResults>(
      `/admin/search?q=${encodeURIComponent(q)}&type=${type}`,
    ),
  /**
   * AI Ops Co-pilot (sprint admin_ai_copilot S1, 28/05/26) — sudo-only chat
   * cross-tenant per query operative (billing, trial, users, finance).
   * Sessions persistite in Firestore copilot_sessions/.
   */
  copilot: {
    chat: (message: string, sessionId?: string) =>
      authedMutate<{
        sessionId: string;
        messageId: string;
        text: string;
        statusMessages: string[];
        tokenUsage?: { promptTokens: number; completionTokens: number; totalTokens: number };
      }>('POST', '/admin/copilot/chat', { message, ...(sessionId ? { sessionId } : {}) }),
    listSessions: (limit = 50) =>
      authedJson<{
        items: Array<{
          sessionId: string;
          sudoUid: string;
          sudoEmail: string | null;
          createdAt: string;
          lastMessageAt: string;
          messageCount: number;
          totalTokens: number;
          title: string | null;
        }>;
        count: number;
      }>(`/admin/copilot/sessions?limit=${limit}`),
    listMessages: (sessionId: string) =>
      authedJson<{
        items: Array<{
          messageId: string;
          role: 'user' | 'assistant';
          content: string;
          tokenUsage?: { promptTokens: number; completionTokens: number; totalTokens: number };
          statusMessages?: string[];
          createdAt: string;
        }>;
        count: number;
      }>(`/admin/copilot/sessions/${encodeURIComponent(sessionId)}/messages`),
  },
  /**
   * Triggers a CSV download. Resolves with a Blob — caller hands it to
   * the browser via a temporary <a> tag. We deliberately don't navigate
   * the window because the request must include the Bearer token.
   */
  exportCsv: async (
    endpoint: 'customers' | 'subscriptions' | 'postings' | 'applications' | 'users' | 'admin-actions',
    filters: Record<string, unknown> = {},
  ): Promise<Blob> => {
    const url = `/admin/${endpoint}${buildQueryString({ ...filters, format: 'csv' })}`;
    const res = await authedFetch(url);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`CSV export ${res.status}: ${text}`);
    }
    return res.blob();
  },
};
